import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  Truck,
  Plus,
  Search,
  MapPin,
  ChevronDown,
  ChevronRight,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Play,
  Ban,
  Layers,
  Navigation,
  User,
  Car,
  Users,
  Calendar,
  RotateCcw,
  Flag,
  Ticket,
  Home,
  Printer,
  PackageCheck,
  History,
  FileDigit
} from "lucide-react";

import Loader from "../../loader/Loader";
import Pagination from "../Pagination";
import { domain, domainAccounting } from "../../../security";
import { selectUserID, selectFullName } from "../../../redux/IchthusSlice";
import CreateShipmentModal from "./CreateShipmentModal";
import { ShipmentTripTicketModal } from "./ShipmentTripTicketPrint";
import TripTicketNumberSeries from "./TripTicketNumberSeries";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const normalizeStatus = (s) =>
  (s || "")
    .toString()
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(val || 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const getLocalISOString = () => {
  const tzOffset = new Date().getTimezoneOffset() * 60000;
  return new Date(Date.now() - tzOffset).toISOString().slice(0, 16);
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGES
// ─────────────────────────────────────────────────────────────

const ShipmentStatusBadge = ({ status }) => {
  const s = normalizeStatus(status);
  const map = {
    pending: {
      color: "bg-amber-50 text-amber-700 border-amber-200",
      icon: <Clock size={11} />,
      label: "Pending",
    },
    intransit: {
      color: "bg-blue-50 text-blue-700 border-blue-200",
      icon: <Truck size={11} />,
      label: "In Transit",
    },
    completed: {
      color: "bg-green-50 text-green-700 border-green-200",
      icon: <CheckCircle size={11} />,
      label: "Completed",
    },
    cancelled: {
      color: "bg-red-50 text-red-700 border-red-200",
      icon: <XCircle size={11} />,
      label: "Cancelled",
    },
  };
  const cfg = map[s] || {
    color: "bg-gray-50 text-gray-600 border-gray-200",
    icon: <AlertCircle size={11} />,
    label: status,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const StopStatusBadge = ({ status }) => {
  const s = normalizeStatus(status);
  const map = {
    pending: { color: "bg-gray-100 text-gray-600", label: "Pending" },
    arrived: { color: "bg-blue-100 text-blue-700", label: "Arrived" },
    completed: { color: "bg-green-100 text-green-700", label: "Done" },
  };
  const cfg = map[s] || { color: "bg-gray-100 text-gray-500", label: status };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const TaskItemStatusBadge = ({ status }) => {
  const s = normalizeStatus(status);
  if (s === "delivered" || s === "collected")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
        <CheckCircle size={9} />
        {status}
      </span>
    );
  if (s === "returned" || s === "failed")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200">
        {s === "failed" ? <Ban size={9} /> : <RotateCcw size={9} />}
        {status}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
      <Clock size={9} />
      Pending
    </span>
  );
};

const StatsCard = ({ label, value, colorClass, icon }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
    <div className={`p-2 rounded-xl ${colorClass}`}>
      {React.cloneElement(icon, { size: 18 })}
    </div>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// CONFIRM MODAL
// ─────────────────────────────────────────────────────────────

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmClass,
  icon,
  loading,
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
              {icon}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            </div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
        </div>
        <div className="border-t border-gray-100 bg-gray-50 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-xl transition disabled:opacity-70 shadow-sm ${confirmClass}`}
          >
            {loading && (
              <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ACTION TIME MODAL
// ─────────────────────────────────────────────────────────────

const ActionTimeModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmClass,
  icon,
  loading,
}) => {
  const [actionTime, setActionTime] = useState("");

  useEffect(() => {
    if (isOpen) {
      setActionTime(getLocalISOString());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
              {icon}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            </div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            {message}
          </p>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Select Date & Time *
            </label>
            <input
              type="datetime-local"
              value={actionTime}
              onChange={(e) => setActionTime(e.target.value)}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white outline-none transition focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
          </div>
        </div>
        <div className="border-t border-gray-100 bg-gray-50 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(actionTime)}
            disabled={loading || !actionTime}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-xl transition disabled:opacity-70 shadow-sm ${confirmClass}`}
          >
            {loading && (
              <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STOP ROW
// ─────────────────────────────────────────────────────────────

const StopRow = ({
  stop,
  shipmentStatus,
  onSiteArrive,
  onSiteDepart,
  onItemAction,
  onPrint,
  ship,
  doItems, setDOItems,
}) => {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const canAct = normalizeStatus(shipmentStatus) === "intransit";

  const tasks = [
    ...(stop.deliveryOrders || []).map((d) => ({
      ...d,
      taskType: "DO",
      uniqueId: `DO-${d.deliveryOrderId}`,
      docId: d.deliveryOrderId,
      num: d.deliveryOrderNumber || d.documentNumber,
    })),
    ...(stop.tradeReturns || []).map((t) => ({
      ...t,
      taskType: "TR",
      uniqueId: `TR-${t.tradeReturnId}`,
      docId: t.tradeReturnId,
      num: t.returnNumber || t.documentNumber,
      totalAmount: t.totalRefundAmount || t.totalAmount,
    })),
  ];
  const totalValue = tasks.reduce((a, b) => a + (b.totalAmount || 0), 0);

  const fetchDOItems = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}/api/Shipments/items-to-ship/shipmentId/${ship.id}`);
      return res.data
    } catch (error) {
      console.error('There was a problem adding data:', error);
      return []
    }
  }, [ship]);

  useEffect(() => {
    const generateDOItems = async () => {
      setLoading(true)

      const _doItems = await fetchDOItems()
      
      setDOItems(_doItems)

      setLoading(false)
    }

    if(ship){
      generateDOItems();
    }
  }, [fetchDOItems, ship, setDOItems])

  const totalShipmentValue = doItems.reduce((a, b) => a + (b.lineTotal || 0), 0);

  return (
    <>
      <div
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition rounded-xl mb-1 ${expanded ? "bg-blue-50/60" : "bg-white border border-gray-200 hover:bg-gray-50"}`}
      >
        <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0 hidden">
          {stop.stopOrder}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <MapPin size={13} className="text-blue-500 shrink-0" />
            <div className="flex flex-row gap-2">
              {stop.locations?.map((item, index) => {
                return (
                  <span key={index} className="font-semibold text-sm border border-blue-300 text-blue-800 rounded-md px-2 py-1 bg-blue-100 hidden">
                    {item.locationName}
                  </span>
                )
              })}
              <span className="font-semibold text-sm border border-blue-300 text-blue-800 rounded-md px-2 py-1 bg-blue-100">
                {ship.shippingLocation ?? ""}
              </span>
            </div>
            <StopStatusBadge status={stop.status} />
            {stop.arrivedAt && normalizeStatus(stop.status) !== "completed" && (
              <span className="text-[10px] text-gray-400 hidden md:block">
                Arrived: {formatDateTime(stop.arrivedAt)}
              </span>
            )}
            {stop.completedAt && (
              <span className="text-[10px] text-gray-400 hidden md:block">
                Departed: {formatDateTime(stop.completedAt)}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5 pl-5 hidden">
            {tasks.length} Task{tasks.length !== 1 ? "s" : ""} ·{" "}
            {formatCurrency(totalValue)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5 pl-5">
            {tasks.length} Task{tasks.length !== 1 ? "s" : ""} ·{" "}
            {formatCurrency(totalShipmentValue)}
          </p>
        </div>

        <div
          className="flex items-center gap-2 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {(stop.deliveryOrders || []).length > 0 && onPrint && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrint(stop);
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition shadow-sm"
              title="Print Trip Ticket"
            >
              <Printer size={11} /> Trip Ticket
            </button>
          )}

          {canAct && (
            <>
              {normalizeStatus(stop.status) === "pending" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSiteArrive(stop);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition shadow-sm"
                >
                  <MapPin size={11} /> Site Arrive
                </button>
              )}
              {normalizeStatus(stop.status) === "arrived" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSiteDepart(stop);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition shadow-sm"
                >
                  <Flag size={11} /> Site Depart
                </button>
              )}
            </>
          )}
        </div>

        <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
          {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>

      {expanded && (
        <div>
          {normalizeStatus(stop.status) === "arrived" &&
            <div className="w-full flex justify-end">
              <button
                onClick={(e) => {
                e.stopPropagation();
                
                // Get IDs of all items currently visible in this stop's tasks
                const taskIds = tasks.map(t => t.deliveryOrderId);
                
                setDOItems(prev => prev.map(item => 
                  taskIds.includes(item.deliveryOrderMainId) 
                    ? { ...item, status: "Delivered" } 
                    : item
                ));
              }}
                className="my-2 flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition shadow-sm"
              >
                <PackageCheck size={11} /> Tag All as Delivered
              </button>
            </div>
          }

          <div className="ml-10 mb-3 border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs text-left text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-100 text-[10px] uppercase font-semibold text-gray-500">
                <tr>
                  <th className="px-4 py-2.5">Task Number</th>
                  <th className="px-4 py-2.5">Customer</th>
                  <th className="px-4 py-2.5">Salesman</th>
                  <th className="px-4 py-2.5">Terms</th>
                  <th className="px-4 py-2.5 text-right">Amount</th>
                  <th className="px-4 py-2.5 text-center hidden">Item Status</th>
                  {canAct && <th className="px-4 py-2.5 text-center hidden">Actions</th>}
                  <th className="px-4 py-2.5">Items</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tasks.map((task) => {
                  const _items = doItems?.filter(i => i.deliveryOrderMainId === task?.deliveryOrderId)
                  const _totalValue = _items?.reduce((a, b) => a + (b.lineTotal || 0), 0);

                  return (
                  <tr key={task.uniqueId} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-2.5">
                      <span className="font-bold text-gray-800">{task.num}</span>
                      <span
                        className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${task.taskType === "DO" ? "bg-blue-100 text-blue-600" : "bg-green-100 text-green-600"}`}
                      >
                        {task.taskType}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {task.customerName || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      <div className="flex flex-col gap-3">
                        {task.salesOrders && task.salesOrders.length > 0 ? (
                          task.salesOrders.map((so, idx) => (
                            <div key={idx} className="flex flex-col">
                              {/* Salesman Name */}
                              <span className="text-sm font-bold text-slate-800 leading-none">
                                {so.salesmanName || "Unassigned"}
                              </span>
                              {/* Sales Order Number Badge */}
                              <span className="text-[10px] font-mono text-indigo-600 mt-1">
                                {so.salesOrderNumber}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {task.termsDescription || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium">
                      {formatCurrency(_totalValue)}
                    </td>
                    <td className="px-4 py-2.5 text-center hidden">
                      <TaskItemStatusBadge status={task.itemStatus} />
                    </td>
                    {canAct && (
                      <td className="px-4 py-2.5 text-center hidden">
                        {normalizeStatus(task.itemStatus) === "pending" && (
                          <div className="flex items-center justify-center gap-1.5">
                            {task.taskType === "DO" ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onItemAction(stop, task, "deliver", "DO");
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition"
                                >
                                  <CheckCircle size={10} /> Delivered
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onItemAction(stop, task, "return", "DO");
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition"
                                >
                                  <RotateCcw size={10} /> Return
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onItemAction(stop, task, "collect", "TR");
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                                >
                                  <CheckCircle size={10} /> Collect
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onItemAction(stop, task, "fail", "TR");
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-100 transition"
                                >
                                  <Ban size={10} /> Failed
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm hidden">
                      <ul className="space-y-1.5">
                        {_items?.map((item) => (
                          <li key={item?.id} className="flex items-start gap-2">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                            <div>
                              <span className="block font-semibold text-gray-800 uppercase text-[11px] tracking-wider leading-none">
                                {item.brandName || ""}
                              </span>
                              <span className="text-gray-600">
                                {item.productName ?? "Unknown Product"}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <ul className="space-y-2">
                        {_items?.map((item) => {
                          // Logic: Default to 'Delivered' if no status is set
                          const currentItemStatus = item.status || "Delivered";

                          return (
                            <li
                              key={item?.id}
                              className="flex items-center justify-between gap-4 p-2 rounded-lg border border-gray-50 bg-white/50 shadow-sm"
                            >
                              <div className="flex items-start gap-2">
                                {/* Status Dot */}
                                <span
                                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full transition-colors ${
                                    currentItemStatus === "Delivered" 
                                      ? "bg-green-500" 
                                      : currentItemStatus === "Pending" 
                                        ? "bg-yellow-500" 
                                        : "bg-red-500"
                                  }`}
                                />
                                <div>
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className="block font-semibold text-gray-800 uppercase text-[10px] tracking-wider leading-none">
                                      {item.productName || ""}
                                    </span>
                                    
                                    {/* New Status Text Badge */}
                                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-tighter ${
                                      currentItemStatus === "Delivered" 
                                        ? "bg-green-100 text-green-700" 
                                        : currentItemStatus === "Pending" 
                                          ? "bg-yellow-100 text-yellow-700" 
                                          : "bg-red-100 text-red-700"
                                    }`}>
                                      {currentItemStatus}
                                    </span>
                                  </div>
                                  
                                  <span className="text-gray-600 text-[11px] line-clamp-1">
                                    {item.brandName ?? "Unknown Product"}
                                  </span>
                                </div>
                              </div>

                              {/* Tagging Feature Selector */}
                              {normalizeStatus(stop.status) === "arrived" &&
                                <div className="flex shrink-0">
                                  <select
                                    value={currentItemStatus}
                                    onChange={(e) => {
                                      const newStatus = e.target.value;
                                      setDOItems((prevItems) =>
                                        prevItems.map((doItem) =>
                                          doItem.id === item.id 
                                            ? { ...doItem, status: newStatus } 
                                            : doItem
                                        )
                                      );
                                    }}
                                    className={`text-[10px] font-bold px-2 py-1 rounded-md border transition-all cursor-pointer outline-none
                                      ${
                                        currentItemStatus === "Delivered"
                                          ? "bg-green-50 text-green-700 border-green-200"
                                          : currentItemStatus === "Pending"
                                            ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                            : "bg-red-50 text-red-700 border-red-200"
                                      }`}
                                  >
                                    <option value="Pending">Pending</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Returned">Returned</option>
                                  </select>
                                </div>
                              }
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                  </tr>
                )})}
                {tasks.length === 0 && (
                  <tr>
                    <td
                      colSpan={canAct ? 5 : 4}
                      className="px-4 py-6 text-center text-gray-400 italic"
                    >
                      No tasks assigned to this stop.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading &&
        <div className="flex justify-center items-center py-24">
          <Loader />
        </div>
      }
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const AllShipments = () => {
  const userId = useSelector(selectUserID);
  const userName = useSelector(selectFullName);

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [startTarget, setStartTarget] = useState(null);
  const [plantArriveTarget, setPlantArriveTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const [siteArriveTarget, setSiteArriveTarget] = useState(null);
  const [siteDepartTarget, setSiteDepartTarget] = useState(null);
  const [itemActionTarget, setItemActionTarget] = useState(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [doItems, setDOItems] = useState([]);

  const [tripTicketModal, setTripTicketModal] = useState({
    isOpen: false,
    data: null,
  });

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/Shipments`);
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setShipments(data);
    } catch {
      toast.error("Failed to load shipments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  // ── FIX: Read drivers array from ship.drivers, not ship.driverName ──
  const handlePrintTripTicket = async (ship, stop) => {
    try {
      setLoading(true);
      const doIds = (stop.deliveryOrders || []).map((d) => d.deliveryOrderId);
      
      //const res = await axios.get(`${domain}/api/Shipments/available-dos`);
      // const allDOs = Array.isArray(res.data) ? res.data : [];

      // const items = [];
      // for (const doId of doIds) {
      //   const found = allDOs.find((d) => d.id === doId);
      //   if (found?.deliveryOrderItems) {
      //     found.deliveryOrderItems.forEach((i) => {
      //       items.push({
      //         qty: i.quantityDelivered ?? i.quantity ?? 0,
      //         uom: i.uomName ?? i.unitName ?? "—",
      //         productName: i.productName ?? "—",
      //       });
      //     });
      //   }
      // }

      const res = await axios.post(`${domain}/api/DeliveryOrderMain/items`, {doIds: doIds});
      const allItems = res.data ?? [];

      const items = [];
      allItems.forEach((i) => {
        items.push({
          qty: i.quantityDelivered ?? i.quantity ?? 0,
          uom: i.uomName ?? i.unitName ?? "—",
          productName: i.productName ?? "—",
          brandName: i.brandName ?? "",
        });
      });

      const customerName =
        stop.deliveryOrders?.[0]?.customerName ?? stop.locationName;

      setTripTicketModal({
        isOpen: true,
        data: {
          tripTicketNumber: ship.tripTicketNumber,
          shipmentNumber: ship.shipmentNumber,
          vehicleName: ship.vehicleName,
          plateNumber: ship.plateNumber || ship.PlateNumber || "—",
          // ✅ FIX: drivers is an array of { fullName, ... } from the API
          driverName:
            (ship.drivers || []).map((d) => d.fullName).join(", ") || "—",
          plannedDate: ship.plannedDate,
          startedAt: ship.startedAt,
          completedAt: ship.completedAt,
          customerName,
          // locations: stop.locations,
          locations: [{locationName: ship?.shippingLocation || ""}],
          siteArrivedAt: stop.arrivedAt,
          siteDepartedAt: stop.completedAt,
          preparedByName: userName,
          items,
        },
      });
    } catch (error) {
      console.error('There was a problem adding data:', error);
      toast.error("Failed to load trip ticket data.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartConfirm = async (actionTime) => {
    if (!startTarget) return;
    setActionLoading(true);
    try {
      await axios.post(`${domain}/api/Shipments/${startTarget.id}/start`, {
        actionTime,
      });
      toast.success("Plant Departure logged successfully!");
      fetchShipments();
      setStartTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start shipment.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSiteArriveConfirm = async (actionTime) => {
    if (!siteArriveTarget) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${domain}/api/Shipments/${siteArriveTarget.shipmentId}/stops/${siteArriveTarget.stop.id}/arrive`,
        { actionTime },
      );

      const locations = siteArriveTarget.stop.locations
          .map(loc => loc.locationName)
          .join(", ");

      toast.success(`Arrived at ${locations}`);
      fetchShipments();
      setSiteArriveTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to log arrival.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleThrowToAccounting = async () => {
    if (!plantArriveTarget) return;

    const dataToPost = {
      date: plantArriveTarget?.plannedDate || null,
      shipmentNumber: plantArriveTarget?.shipmentNumber || "",
      driver: plantArriveTarget?.drivers?.[0]?.fullName || "",
      plateNo: plantArriveTarget?.plateNumber || "",
      timeDeparture: plantArriveTarget?.startedAt || null,
      shipmentDetails: plantArriveTarget?.stops
        ?.flatMap(stop =>
          (stop.deliveryOrders || [])
            .filter(item => item.itemStatus === "Completed")
            .map(item => ({
              deliveryOrderHeaderId: item.documentNumber ?? ""
            }))
        ) || []
    };

    try {
      await axios.post(
        `${domainAccounting}/api/Shipment`, dataToPost ,
      );
      
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSiteDepartConfirm = async (actionTime) => {
    if (!siteDepartTarget) return;
    setActionLoading(true);

    const _data = {
      ...actionTime,
      doItems: doItems
    }
    
    try {
      await axios.post(
        `${domain}/api/Shipments/${siteDepartTarget.shipmentId}/stops/${siteDepartTarget.stop.id}/complete`,
        _data,
      );
      toast.success(
        `Stop ${siteDepartTarget.stop.stopOrder} departed/completed!`,
      );

      fetchShipments();
      setSiteDepartTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to log departure.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlantArriveConfirm = async (actionTime) => {
    if (!plantArriveTarget) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${domain}/api/Shipments/${plantArriveTarget.id}/plant-arrive`,
        { actionTime },
      );
      await handleThrowToAccounting()
      toast.success("Plant Arrival logged. Shipment is now completed!");
      fetchShipments();
      setPlantArriveTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to log Plant Arrive.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleItemActionConfirm = async () => {
    if (!itemActionTarget) return;
    setActionLoading(true);
    const { shipmentId, stop, task, type, docType } = itemActionTarget;
    try {
      if (docType === "DO") {
        await axios.post(
          `${domain}/api/Shipments/${shipmentId}/stops/${stop.id}/do/${task.docId}/${type}`,
        );
        toast.success(
          type === "deliver" ? "Marked as delivered!" : "Marked as returned!",
        );
      } else {
        await axios.post(
          `${domain}/api/Shipments/${shipmentId}/stops/${stop.id}/tr/${task.docId}/${type}`,
        );
        toast.success(
          type === "collect" ? "Collection successful!" : "Failed to collect.",
        );
      }
      fetchShipments();
      setItemActionTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setActionLoading(true);
    try {
      await axios.post(`${domain}/api/Shipments/${cancelTarget.id}/cancel`);
      toast.success("Shipment cancelled. Tasks reverted to pending.");
      fetchShipments();
      setCancelTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel.");
    } finally {
      setActionLoading(false);
    }
  };

  const getItemModalProps = () => {
    if (!itemActionTarget) return {};
    const { stop, type, docType, task } = itemActionTarget;
    const isDO = docType === "DO";
    
    const foundOrder = stop.deliveryOrders?.find(i => i.deliveryOrderId === task?.deliveryOrderId)
    const locations = foundOrder?.locations?.map(loc => loc.locationName)
          .join(", ");

    if (isDO) {
      return {
        title: type === "deliver" ? "Confirm Delivery" : "Confirm Return",
        message:
          type === "deliver"
            ? `Mark this delivery order as successfully delivered at ${locations}?`
            : `Mark this delivery order as returned? This will trigger the returns module.`,
        confirmLabel: type === "deliver" ? "Yes, Delivered" : "Yes, Returned",
        confirmClass:
          type === "deliver"
            ? "bg-green-600 hover:bg-green-700"
            : "bg-red-600 hover:bg-red-700",
        icon:
          type === "deliver" ? (
            <CheckCircle size={22} className="text-green-600" />
          ) : (
            <RotateCcw size={22} className="text-red-500" />
          ),
      };
    } else {
      return {
        title: type === "collect" ? "Confirm Collection" : "Collection Failed",
        message:
          type === "collect"
            ? `Mark this trade return as successfully collected at ${locations}?`
            : `Mark this collection as failed?`,
        confirmLabel: type === "collect" ? "Yes, Collected" : "Yes, Failed",
        confirmClass:
          type === "collect"
            ? "bg-blue-600 hover:bg-blue-700"
            : "bg-orange-600 hover:bg-orange-700",
        icon:
          type === "collect" ? (
            <CheckCircle size={22} className="text-blue-600" />
          ) : (
            <Ban size={22} className="text-orange-500" />
          ),
      };
    }
  };

  const tabs = [
    { id: "All", label: "All", icon: <Layers size={14} /> },
    { id: "Pending", label: "Pending", icon: <Clock size={14} /> },
    { id: "InTransit", label: "In Transit", icon: <Truck size={14} /> },
    { id: "Completed", label: "Completed", icon: <CheckCircle size={14} /> },
    { id: "Cancelled", label: "Cancelled", icon: <XCircle size={14} /> },
  ];

  const tabMatch = (s, tab) => {
    if (tab === "All") return true;
    return normalizeStatus(s.status) === normalizeStatus(tab);
  };

  const filtered = shipments.filter((s) => {
    const matchTab = tabMatch(s, activeTab);
    const term = searchTerm.toLowerCase();

    // ✅ FIX: Search across the drivers/personnel arrays, not flat string properties
    const matchSearch =
      !term ||
      (s.shipmentNumber || "").toLowerCase().includes(term) ||
      (s.tripTicketNumber || "").toLowerCase().includes(term) ||
      (s.vehicleName || "").toLowerCase().includes(term) ||
      (s.plateNumber || s.PlateNumber || "").toLowerCase().includes(term) ||
      (s.drivers || []).some((d) =>
        (d.fullName || "").toLowerCase().includes(term),
      ) ||
      (s.personnel || []).some((p) =>
        (p.fullName || "").toLowerCase().includes(term),
      );

    return matchTab && matchSearch;
  });

  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const tabCount = (tab) => shipments.filter((s) => tabMatch(s, tab)).length;

  const stats = {
    total: shipments.length,
    inTransit: shipments.filter(
      (s) => normalizeStatus(s.status) === "intransit",
    ).length,
    completed: shipments.filter(
      (s) => normalizeStatus(s.status) === "completed",
    ).length,
    totalTasks: shipments.reduce((a, b) => a + (b.totalTasks || 0), 0),
  };

  const getProgress = (s) => {
    const stops = s.stops || [];
    if (!stops.length) return 0;
    const done = stops.filter(
      (st) => normalizeStatus(st.status) === "completed",
    ).length;
    return Math.round((done / stops.length) * 100);
  };

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const handleSyncToAccounting = async (dateFrom, dateTo) => {
    if (shipments.length <= 0) {
      toast.error(`No data to Sync.`);
      return;
    }

    setLoading(true);

    try {
      const getDateOnly = (value) => {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };

      const from = getDateOnly(dateFrom);
      const to = getDateOnly(dateTo);

      const filtered = shipments.filter((i) => {
        const date = getDateOnly(i.completedAt);
        return date >= from && date <= to;
      });

      const finalData = filtered.map((ship) => {
        return {
          date: ship?.plannedDate || null,
          shipmentNumber: ship?.shipmentNumber || "",
          driver: ship?.drivers?.[0]?.fullName || "",
          plateNo: ship?.plateNumber || "",
          timeDeparture: ship?.startedAt || null,
          shipmentDetails: ship?.stops
            ?.flatMap(stop =>
              (stop.deliveryOrders || [])
                .filter(item => item.itemStatus === "Completed")
                .map(item => ({
                  deliveryOrderHeaderId: item.documentNumber ?? ""
                }))
            ) || []
        }
      });

      await axios.post(
        `${domainAccounting}/api/Shipment/bulk`, finalData ,
      );

      
      
    toast.success(`${finalData.length} shipments synced to accounting.`);

    } catch (error) {
      console.error(error);
      toast.error(`An error occurred during preparation.`);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const [showTripTicketNumberSeriesForm, setShowTripTicketNumberSeriesForm] = useState(false);
  const openTripTricketNumberSeriesForm = () => {
    setShowTripTicketNumberSeriesForm(true)
  }
  const closeTripTricketNumberSeriesForm = () => {
    setShowTripTicketNumberSeriesForm(false)
  }

  return (
    <div className="min-h-screen pb-12 bg-gray-50 relative z-0">
      {/* ──────────────── MODALS ──────────────── */}
      <ActionTimeModal
        isOpen={!!startTarget}
        onClose={() => setStartTarget(null)}
        onConfirm={handleStartConfirm}
        loading={actionLoading}
        title="Plant Departure"
        message={`Log the exact date and time the truck left the plant.`}
        confirmLabel="Confirm Departure"
        confirmClass="bg-blue-600 hover:bg-blue-700"
        icon={<Play size={22} className="text-blue-600" />}
      />
      <ActionTimeModal
        isOpen={!!siteArriveTarget}
        onClose={() => setSiteArriveTarget(null)}
        onConfirm={handleSiteArriveConfirm}
        loading={actionLoading}
        title="Site Arrive"
        message={`Log the exact date and time the vehicle arrived.`}
        confirmLabel="Confirm Arrival"
        confirmClass="bg-blue-600 hover:bg-blue-700"
        icon={<MapPin size={22} className="text-blue-600" />}
      />
      <ActionTimeModal
        isOpen={!!siteDepartTarget}
        onClose={() => setSiteDepartTarget(null)}
        onConfirm={handleSiteDepartConfirm}
        loading={actionLoading}
        title="Site Depart"
        message={`Log the exact date and time the vehicle departed.`}
        confirmLabel="Confirm Departure"
        confirmClass="bg-green-600 hover:bg-green-700"
        icon={<Flag size={22} className="text-green-600" />}
      />
      <ActionTimeModal
        isOpen={!!plantArriveTarget}
        onClose={() => setPlantArriveTarget(null)}
        onConfirm={handlePlantArriveConfirm}
        loading={actionLoading}
        title="Plant Arrival"
        message={`Log the exact date and time the truck returned to the plant.`}
        confirmLabel="Confirm Return to Plant"
        confirmClass="bg-green-600 hover:bg-green-700"
        icon={<Home size={22} className="text-green-600" />}
      />
      <ConfirmModal
        isOpen={!!itemActionTarget}
        onClose={() => setItemActionTarget(null)}
        onConfirm={handleItemActionConfirm}
        loading={actionLoading}
        {...getItemModalProps()}
      />
      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        loading={actionLoading}
        title="Cancel Shipment"
        message={`Cancelling will revert all tasks back to pending.`}
        confirmLabel="Cancel Shipment"
        confirmClass="bg-red-600 hover:bg-red-700"
        icon={<Ban size={22} className="text-red-500" />}
      />

      <ShipmentTripTicketModal
        isOpen={tripTicketModal.isOpen}
        onClose={() => setTripTicketModal({ isOpen: false, data: null })}
        data={tripTicketModal.data}
      />

      {showCreateModal && (
        <CreateShipmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            fetchShipments();
            setShowCreateModal(false);
          }}
          currentUserId={userId}
        />
      )}

      {/* ── Header & Tabs ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <Navigation size={22} className="text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Shipments</h1>
                <p className="text-gray-500 text-sm mt-0.5">
                  Route management · Multi-stop delivery & pickup tracking
                </p>
              </div>
            </div>
            <div className="flex flex-row gap-3">
              <button
                onClick={() => openSyncToAccountingForm()}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Sync To Accounting</span>
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition shadow-sm"
              >
                <Plus size={16} /> New Shipment
              </button>
            </div>
          </div>

          <div className="py-2">
          <button
            onClick={openTripTricketNumberSeriesForm}
            className="group flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
              <FileDigit size={18} />
            </div>

            <div className="hidden sm:flex flex-col items-start">
              <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">
                Trip Ticket Number Series
              </span>
              <span className="text-[11px] text-gray-400">
                Manage numbering sequence
              </span>
            </div>
          </button>
        </div>

          <div className="flex gap-1 mt-5 p-1 bg-gray-100 rounded-xl max-w-max overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === tab.id ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.id ? "bg-indigo-50 text-indigo-600" : "bg-gray-200 text-gray-500"}`}
                >
                  {tabCount(tab.id)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <StatsCard
            label="Total Shipments"
            value={stats.total}
            colorClass="bg-gray-100 text-gray-600"
            icon={<Layers />}
          />
          <StatsCard
            label="In Transit"
            value={stats.inTransit}
            colorClass="bg-blue-50 text-blue-600"
            icon={<Truck />}
          />
          <StatsCard
            label="Completed"
            value={stats.completed}
            colorClass="bg-green-50 text-green-600"
            icon={<CheckCircle />}
          />
          <StatsCard
            label="Total Tasks"
            value={stats.totalTasks}
            colorClass="bg-indigo-50 text-indigo-600"
            icon={<Package />}
          />
        </div>

        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by shipment #, trip ticket #, driver, vehicle, or plate number..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all text-sm outline-none"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Loader />
          </div>
        ) : (
          <>
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="w-10 px-5 py-3.5"></th>
                    <th className="px-5 py-3.5 font-semibold">
                      Shipment / Trip Ticket
                    </th>
                    <th className="px-5 py-3.5 font-semibold">
                      Vehicle & Plate
                    </th>
                    <th className="px-5 py-3.5 font-semibold">Driver</th>
                    <th className="px-5 py-3.5 font-semibold">Personnel</th>
                    <th className="px-5 py-3.5 font-semibold">Stops</th>
                    <th className="px-5 py-3.5 font-semibold">Planned Date</th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Status
                    </th>
                    <th className="px-5 py-3.5 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map((ship) => {
                    const progress = getProgress(ship);
                    const isExpanded = expandedId === ship.id;
                    const stops = ship.stops || [];
                    const statusLower = normalizeStatus(ship.status);
                    const plateText =
                      ship.plateNumber || ship.PlateNumber || "";

                    // ✅ FIX: Derive display strings from the arrays
                    const driverText =
                      (ship.drivers || []).map((d) => d.fullName).join(", ") ||
                      "—";
                    const personnelText =
                      (ship.personnel || [])
                        .map((p) => p.fullName)
                        .join(", ") || "—";

                    const allStopsDone =
                      stops.length === 0 ||
                      stops.every(
                        (st) => normalizeStatus(st.status) === "completed",
                      );

                    return (
                      <React.Fragment key={ship.id}>
                        <tr
                          onClick={() =>
                            setExpandedId(isExpanded ? null : ship.id)
                          }
                          className={`hover:bg-indigo-50/20 transition cursor-pointer ${isExpanded ? "bg-indigo-50/10" : ""}`}
                        >
                          <td className="px-5 py-3.5 text-center">
                            <button className="p-1 hover:bg-gray-200 rounded-lg transition text-gray-400">
                              {isExpanded ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-bold text-gray-900">
                              {ship.shipmentNumber}
                            </span>
                            {ship.tripTicketNumber && (
                              <p className="text-[11px] text-indigo-500 font-semibold mt-0.5 flex items-center gap-1">
                                <Ticket size={10} />
                                {ship.tripTicketNumber}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                              <Package size={10} />
                              {ship.totalTasks || 0} Tasks
                            </p>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex flex-col">
                              {plateText && (
                                <div className="text-lg font-bold flex items-center gap-1 text-gray-500 font-mono bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded w-max">
                                  {plateText}
                                </div>
                              )}
                              <div className="flex items-center gap-1.5">
                                <Car size={13} className="text-gray-400" />
                                <span className="font-medium text-gray-700">
                                  {ship.vehicleName || "—"}
                                </span>
                              </div>
                            </div>
                          </td>
                          {/* ✅ FIX: Desktop — Driver column */}
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <User size={13} className="text-gray-400" />
                              <span className="text-gray-700">
                                {driverText}
                              </span>
                            </div>
                          </td>
                          {/* ✅ FIX: Desktop — Personnel column */}
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <Users size={13} className="text-gray-400" />
                              <span className="text-gray-500 text-xs">
                                {personnelText}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-700 font-medium text-xs">
                                {
                                  stops.filter(
                                    (s) =>
                                      normalizeStatus(s.status) === "completed",
                                  ).length
                                }
                                /{stops.length}
                              </span>
                              <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-indigo-500 h-1.5 rounded-full transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-500 text-xs">
                            <div className="flex items-center gap-1">
                              <Calendar size={11} />
                              {formatDate(ship.plannedDate)}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <ShipmentStatusBadge status={ship.status} />
                          </td>
                          <td
                            className="px-5 py-3.5 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center gap-2">
                              {statusLower === "pending" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStartTarget(ship);
                                  }}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition shadow-sm"
                                >
                                  <Play size={11} /> Depart
                                </button>
                              )}
                              {statusLower === "intransit" && allStopsDone && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPlantArriveTarget(ship);
                                  }}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition shadow-sm"
                                >
                                  <Home size={11} /> Arrive
                                </button>
                              )}
                              {(statusLower === "pending" ||
                                statusLower === "intransit") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCancelTarget(ship);
                                  }}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition shadow-sm"
                                >
                                  <Ban size={11} /> Cancel
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td
                              colSpan="9"
                              className="bg-slate-50/70 px-6 py-4"
                            >
                              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                <h4 className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <Navigation
                                    size={13}
                                    className="text-indigo-500"
                                  />{" "}
                                  Route Timeline
                                </h4>

                                <div className="space-y-2">
                                  {/* 1. PLANT DEPARTURE */}
                                  <div
                                    className={`flex items-center justify-between px-4 py-3 rounded-xl ${statusLower === "pending" ? "bg-blue-50/60 border border-blue-200" : "bg-gray-50 border border-gray-200"}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${statusLower === "pending" ? "bg-blue-100 text-blue-600" : "bg-gray-200 text-gray-500"}`}
                                      >
                                        <Play size={16} />
                                      </div>
                                      <div>
                                        <span
                                          className={`font-bold text-sm ${statusLower === "pending" ? "text-blue-900" : "text-gray-700"}`}
                                        >
                                          Plant Departure
                                        </span>
                                        {ship.startedAt ? (
                                          <p className="text-xs text-gray-500 mt-0.5">
                                            Departed:{" "}
                                            {formatDateTime(ship.startedAt)}
                                          </p>
                                        ) : (
                                          <p className="text-xs text-blue-700 mt-0.5">
                                            Awaiting plant departure.
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    {statusLower === "pending" && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setStartTarget(ship);
                                        }}
                                        className="flex gap-1.5 px-3 py-1.5 text-xs font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
                                      >
                                        <Play size={14} /> Depart Plant
                                      </button>
                                    )}
                                  </div>

                                  {/* 2 & 3. CUSTOMER STOPS */}
                                  <div className="pl-6 space-y-2 border-l-2 border-dashed border-gray-200 ml-4 py-2">
                                    {stops.length > 0 ? (
                                      stops
                                        .sort(
                                          (a, b) => a.stopOrder - b.stopOrder,
                                        )
                                        .map((stop) => (
                                          <StopRow
                                            key={stop.id}
                                            stop={stop}
                                            shipmentStatus={ship.status}
                                            shipmentId={ship.id}
                                            onSiteArrive={(stop) =>
                                              setSiteArriveTarget({
                                                shipmentId: ship.id,
                                                stop,
                                              })
                                            }
                                            onSiteDepart={(stop) =>
                                              setSiteDepartTarget({
                                                shipmentId: ship.id,
                                                stop,
                                              })
                                            }
                                            onItemAction={(
                                              stop,
                                              task,
                                              type,
                                              docType,
                                            ) =>
                                              setItemActionTarget({
                                                shipmentId: ship.id,
                                                stop,
                                                task,
                                                type,
                                                docType,
                                              })
                                            }
                                            onPrint={(stop) =>
                                              handlePrintTripTicket(ship, stop)
                                            }
                                            ship={ship}
                                            doItems={doItems} setDOItems={setDOItems}
                                          />
                                        ))
                                    ) : (
                                      <p className="text-center text-gray-400 italic py-4 text-xs bg-white rounded-lg border border-dashed border-gray-200">
                                        No customer stops configured.
                                      </p>
                                    )}
                                  </div>

                                  {/* 4. PLANT ARRIVAL */}
                                  <div
                                    className={`flex items-center justify-between px-4 py-3 rounded-xl ${statusLower === "completed" ? "bg-green-50/60 border border-green-200" : allStopsDone && statusLower === "intransit" ? "bg-green-50 border border-green-200" : "bg-gray-50 border border-gray-200"}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${statusLower === "completed" ? "bg-green-100 text-green-600" : "bg-gray-200 text-gray-500"}`}
                                      >
                                        <Home size={16} />
                                      </div>
                                      <div>
                                        <span
                                          className={`font-bold text-sm ${statusLower === "completed" ? "text-green-900" : "text-gray-700"}`}
                                        >
                                          Plant Arrival
                                        </span>
                                        {statusLower === "completed" &&
                                        ship.completedAt ? (
                                          <p className="text-xs text-green-700 mt-0.5">
                                            Arrived:{" "}
                                            {formatDateTime(ship.completedAt)}
                                          </p>
                                        ) : (
                                          <p className="text-xs text-gray-500 mt-0.5">
                                            Record final plant return once all
                                            stops finish.
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    {statusLower === "intransit" &&
                                      allStopsDone && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPlantArriveTarget(ship);
                                          }}
                                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-sm"
                                        >
                                          <Home size={14} /> Arrive at Plant
                                        </button>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {paginated.length === 0 && (
                    <tr>
                      <td colSpan="9" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <Navigation size={22} className="text-gray-400" />
                          </div>
                          <p className="text-gray-700 font-semibold">
                            No shipments found
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Mobile Cards ── */}
            <div className="lg:hidden space-y-3">
              {paginated.map((ship) => {
                const isExpanded = expandedId === ship.id;
                const stops = ship.stops || [];
                const statusLower = normalizeStatus(ship.status);
                const allStopsDone =
                  stops.length === 0 ||
                  stops.every(
                    (st) => normalizeStatus(st.status) === "completed",
                  );

                const plateText = ship.plateNumber || ship.PlateNumber || "";

                // ✅ FIX: Derive display strings from the arrays
                const driverText =
                  (ship.drivers || []).map((d) => d.fullName).join(", ") || "—";

                return (
                  <div
                    key={ship.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                  >
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : ship.id)}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="p-1.5 bg-indigo-50 rounded-lg shrink-0">
                            <Navigation size={15} className="text-indigo-600" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-gray-900 text-sm">
                              {ship.shipmentNumber}
                            </h3>
                            <div className="flex flex-wrap items-center gap-1 mt-0.5">
                              {/* ✅ FIX: Mobile — use driverText from drivers array */}
                              <p className="text-xs text-gray-500 truncate max-w-full">
                                {driverText} · {ship.vehicleName || "—"}
                              </p>
                              {plateText && (
                                <span className="font-mono text-[10px] text-gray-500 bg-gray-100 border border-gray-200 px-1 rounded truncate">
                                  {plateText}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <ShipmentStatusBadge status={ship.status} />
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-2">
                        {/* MOBILE 1. PLANT DEPARTURE */}
                        <div
                          className={`flex items-center justify-between px-3 py-2.5 rounded-xl ${statusLower === "pending" ? "bg-blue-50/60 border border-blue-200" : "bg-gray-100 border border-gray-200"}`}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${statusLower === "pending" ? "bg-blue-100 text-blue-600" : "bg-gray-200 text-gray-500"}`}
                            >
                              <Play size={14} />
                            </div>
                            <div>
                              <span
                                className={`font-bold text-xs ${statusLower === "pending" ? "text-blue-900" : "text-gray-700"}`}
                              >
                                Plant Departure
                              </span>
                              {ship.startedAt && (
                                <p className="text-[10px] text-gray-500 mt-0.5">
                                  {formatDateTime(ship.startedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                          {statusLower === "pending" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setStartTarget(ship);
                              }}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold bg-blue-600 text-white rounded-lg shadow-sm"
                            >
                              <Play size={11} /> Depart
                            </button>
                          )}
                        </div>

                        {/* MOBILE 2 & 3. CUSTOMER STOPS */}
                        <div className="pl-4 space-y-2 border-l-2 border-dashed border-gray-200 ml-3 py-1">
                          {stops
                            .sort((a, b) => a.stopOrder - b.stopOrder)
                            .map((stop) => (
                              <StopRow
                                key={stop.id}
                                stop={stop}
                                shipmentStatus={ship.status}
                                shipmentId={ship.id}
                                onSiteArrive={(stop) =>
                                  setSiteArriveTarget({
                                    shipmentId: ship.id,
                                    stop,
                                  })
                                }
                                onSiteDepart={(stop) =>
                                  setSiteDepartTarget({
                                    shipmentId: ship.id,
                                    stop,
                                  })
                                }
                                onItemAction={(stop, task, type, docType) =>
                                  setItemActionTarget({
                                    shipmentId: ship.id,
                                    stop,
                                    task,
                                    type,
                                    docType,
                                  })
                                }
                                onPrint={(stop) =>
                                  handlePrintTripTicket(ship, stop)
                                }
                                ship={ship}
                                doItems={doItems} setDOItems={setDOItems}
                              />
                            ))}
                        </div>

                        {/* MOBILE 4. PLANT ARRIVAL */}
                        <div
                          className={`flex items-center justify-between px-3 py-2.5 rounded-xl ${statusLower === "completed" ? "bg-green-50/60 border border-green-200" : allStopsDone && statusLower === "intransit" ? "bg-green-50 border border-green-200" : "bg-gray-100 border border-gray-200"}`}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${statusLower === "completed" ? "bg-green-100 text-green-600" : "bg-gray-200 text-gray-500"}`}
                            >
                              <Home size={14} />
                            </div>
                            <div>
                              <span
                                className={`font-bold text-xs ${statusLower === "completed" ? "text-green-900" : "text-gray-700"}`}
                              >
                                Plant Arrival
                              </span>
                              {statusLower === "completed" &&
                                ship.completedAt && (
                                  <p className="text-[10px] text-green-700 mt-0.5">
                                    {formatDateTime(ship.completedAt)}
                                  </p>
                                )}
                            </div>
                          </div>
                          {statusLower === "intransit" && allStopsDone && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPlantArriveTarget(ship);
                              }}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold bg-green-600 text-white rounded-lg shadow-sm"
                            >
                              <Home size={11} /> Arrive
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-5">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </>
        )}
      </div>

      <SyncToAccountingModal 
        isOpen={showSyncToAccountingForm}
        onClose={closeSyncToAccountingForm}
        onConfirm={handleSyncToAccounting}
      />

      <TripTicketNumberSeries 
        visible={showTripTicketNumberSeriesForm}
        onClose={closeTripTricketNumberSeriesForm}
      />
    </div>
  );
};


const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(dateFrom, dateTo);
    onClose();
  };
  
  const inputClass =
    "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
  const labelClass =
    "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
            <strong>Please fill up fields to filter data</strong>
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className={labelClass}>
                Order Date From
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
            <div>
              <label className={labelClass}>
                Order Date To
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllShipments;
